/** The three everyday ratings a viewer can switch on or off. "Unfiltered (extreme)" is separate: it is the existing
 * Unfiltered Content switch, and applies to posts flagged `isNsfwu`. */
export type FilterRating = "safe" | "questionable" | "explicit"

export const ALL_FILTER_RATINGS: FilterRating[] = ["safe", "questionable", "explicit"]

type RatedPost = { rating?: string | null; isNsfwu?: boolean; site?: string | null }

/** Danbooru sends single letters where "s" means sensitive (suggestive), while on most other boorus "s" means safe. */
const SENSITIVE_LETTER_SITES = new Set(["danbooru"])

/** Turns whatever a source calls a rating into one of the app's categories. Sources disagree ("general" on Safebooru,
 * "sensitive" on Danbooru, single letters on others). Anything missing or unrecognised counts as explicit, matching how
 * the sources' own rating mapping already treats it. */
export function normalizePostRating(raw: string | null | undefined, site?: string | null): FilterRating | "unfiltered" {
  const value = String(raw ?? "").trim().toLowerCase()
  if (value === "unfiltered") return "unfiltered"
  if (value === "s" && site && SENSITIVE_LETTER_SITES.has(site)) return "questionable"
  if (["safe", "general", "s", "g", "sfw", "rated_g"].includes(value)) return "safe"
  if (["questionable", "sensitive", "q"].includes(value)) return "questionable"
  return "explicit"
}

/** Whether a post stays visible for the ratings the viewer has switched on.
 * - Extreme posts (flagged isNsfwu, or rated "unfiltered") follow the Unfiltered Content switch only.
 * - With all three everyday ratings on, nothing else is removed (so posts with no rating still show).
 * - Otherwise a post stays only if its own rating is one of the switched-on ones. */
export function postMatchesRatings(post: RatedPost, activeRatings: FilterRating[], unfilteredAllowed: boolean): boolean {
  const category = post.isNsfwu ? "unfiltered" : normalizePostRating(post.rating, post.site)
  if (category === "unfiltered") return unfilteredAllowed
  if (ALL_FILTER_RATINGS.every((rating) => activeRatings.includes(rating))) return true
  return activeRatings.includes(category)
}

/** Cleans a stored selection: drops anything unknown and falls back to all three if nothing valid is left. */
export function sanitizeActiveRatings(value: unknown): FilterRating[] {
  const list = Array.isArray(value) ? value.filter((item): item is FilterRating => ALL_FILTER_RATINGS.includes(item as FilterRating)) : []
  const unique = Array.from(new Set(list))
  return unique.length ? unique : [...ALL_FILTER_RATINGS]
}
